import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { X, RotateCcw, Check, ChevronDown, ChevronUp, Type, BookmarkPlus, Pipette, Upload, RefreshCw, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from '@/utils/axiosInstance';
import { BASE_URL } from '@/config/constants';
import PreviewContext from '@/context/PreviewContext';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';

// ── Font roles ───────────────────────────────────────────────────────────────
interface ColorKeyDef { key: string; label: string; }
interface FontRoleDef { fontKey: string; weightKey: string; colorKeys: ColorKeyDef[]; label: string; isGradient?: boolean; }

const FONT_ROLES: FontRoleDef[] = [
  {
    fontKey: '--font-heading', weightKey: '--font-heading-weight',
    colorKeys: [
      { key: '--heading-gradient-from', label: 'Start' },
      { key: '--heading-gradient-mid',  label: 'Mid'   },
      { key: '--heading-gradient-to',   label: 'End'   },
    ],
    label: 'Heading',
    isGradient: true,
  },
  {
    fontKey: '--font-display', weightKey: '--font-display-weight',
    colorKeys: [{ key: '--text-normal', label: 'Color' }],
    label: 'Display',
  },
  {
    fontKey: '--font-tertiary', weightKey: '--font-tertiary-weight',
    colorKeys: [{ key: '--text-secondary', label: 'Color' }],
    label: 'Tertiary',
  },
  {
    fontKey: '--font-body', weightKey: '--font-body-weight',
    colorKeys: [{ key: '--text-general', label: 'Color' }],
    label: 'Body',
  },
];

// ── Standalone colour keys shown in the Colours panel ───────────────────────
// --gradient-from/mid/to → primary button gradient (.primary-button)
// --heading-gradient-*   → heading text gradient (controlled in Typography panel)
// --primary              → buttons, links, accent highlights
// --accent               → scroll indicator, accent highlights
// --background1/2        → page backgrounds
// (text colours --text-normal/secondary/general/description are controlled
//  in Typography via their respective font cards)
const COLOR_KEYS: { key: string; label: string; group: string }[] = [
  { key: '--primary',          label: 'Accent / Buttons / Play / Cursor',  group: 'Accent'      },
  { key: '--background1',      label: 'Background main',          group: 'Background'  },
  { key: '--background2',      label: 'Background alt',           group: 'Background'  },
  { key: '--icon-general',     label: 'Icons',                   group: 'Icons'       },
];

const PRIMARY_BUTTON_GRADIENT: { key: string; label: string }[] = [
  { key: '--gradient-from', label: 'Start' },
  { key: '--gradient-mid',  label: 'Mid'   },
  { key: '--gradient-to',   label: 'End'   },
];

const ACCENT_GRADIENT: { key: string; label: string }[] = [
  { key: '--accent-from', label: 'Start' },
  { key: '--accent-mid',  label: 'Mid'   },
  { key: '--accent-to',   label: 'End'   },
];

const COLOR_GROUPS = Array.from(new Set(COLOR_KEYS.map(c => c.group)));

const FONT_WEIGHTS = ['300', '400', '500', '600', '700', '800'] as const;
type RadiusMode = 'default' | 'rounded' | 'square' | 'custom';
type RadiusToken = '--button-radius' | '--card-radius' | '--media-radius' | '--input-radius' | '--tab-radius';
interface RadiusGroupDef { key: RadiusToken; label: string; description: string; roundedValue: string; defaultCustom: number; }

const RADIUS_GROUPS: RadiusGroupDef[] = [
  { key: '--button-radius', label: 'Action buttons', description: 'CTAs, submit and load-more buttons', roundedValue: '9999px', defaultCustom: 12 },
  { key: '--card-radius', label: 'Content cards', description: 'Showcase, events, awards and contact cards', roundedValue: '24px', defaultCustom: 24 },
  { key: '--media-radius', label: 'Images & media', description: 'Portraits, thumbnails and gallery frames', roundedValue: '24px', defaultCustom: 24 },
  { key: '--input-radius', label: 'Inputs & selects', description: 'Form fields, text areas and select triggers', roundedValue: '16px', defaultCustom: 12 },
  { key: '--tab-radius', label: 'Tabs & chips', description: 'Categories, filters and compact labels', roundedValue: '16px', defaultCustom: 12 },
];

function getRadiusMode(value: string | undefined, roundedValue: string): RadiusMode {
  if (!value) return 'default';
  if (value === roundedValue) return 'rounded';
  if (value === '0px') return 'square';
  return 'custom';
}

function getCustomRadius(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 48 ? parsed : fallback;
}

// ── Section text-size scale controls ─────────────────────────────────────────
// Values are proportional multipliers (0.5–1.5) stored in customSections:
// { [sectionKey]: { [roleKey]: '1.2' } } with roleKey ∈ heading/display/tertiary/body.
// Absent key = theme default (100%).
const SIZE_SECTIONS: { key: string; label: string; hideRoles?: string[] }[] = [
  { key: 'hero', label: 'Hero' },
  { key: 'about', label: 'About', hideRoles: ['display'] },
  { key: 'tours', label: 'Events' },
  { key: 'showcase', label: 'Showcase' },
  { key: 'gallery', label: 'Gallery', hideRoles: ['display'] },
  { key: 'awards', label: 'Awards' },
  { key: 'infokit', label: 'Info Kit' },
  // Management-only sections (rendered only on management websites)
  { key: 'services', label: 'Services' },
  { key: 'artistRoster', label: 'Artist Roster' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'clients', label: 'Our Clients' },
  { key: 'team', label: 'Team' },
  { key: 'contact', label: 'Contact' },
  { key: 'navigation', label: 'Navigation', hideRoles: ['heading', 'tertiary', 'body'] },
  { key: 'footer', label: 'Footer', hideRoles: ['display', 'tertiary']  },
  { key: 'audioBar', label: 'Audio bar' },
];
const SIZE_ROLES: { key: string; label: string }[] = [
  { key: 'heading', label: 'Heading' },
  { key: 'display', label: 'Display' },
  { key: 'tertiary', label: 'Tertiary' },
  { key: 'body', label: 'Body' },
];
// Global scale for ALL primary buttons — one slider, site-wide. Stored in the
// fonts map like other component tokens (same pattern as --bg-image-opacity).
const BUTTON_SCALE_KEY = '--button-font-scale';
const SIZE_MIN = 50;
const SIZE_MAX = 150;

// ── Per-section background image controls ────────────────────────────────────
// Stored in UserTheme.customSectionBackgrounds:
// { [sectionKey]: { image: <Cloudinary URL>, opacity: <0-100> } }.
// Absent key = theme default (flat section colours; hero keeps the theme's
// default .bg-img image). The hero entry is mirrored to the legacy
// customBackgroundImage field on save.
const SECTION_BACKGROUNDS: { key: string; label: string }[] = [
  { key: 'hero', label: 'Hero' },
  { key: 'about', label: 'About' },
  { key: 'tours', label: 'Events' },
  { key: 'showcase', label: 'Showcase' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'awards', label: 'Awards' },
  { key: 'infokit', label: 'Info Kit' },
  { key: 'services', label: 'Services' },
  { key: 'artistRoster', label: 'Artist Roster' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'clients', label: 'Our Clients' },
  { key: 'team', label: 'Team' },
  { key: 'contact', label: 'Contact' },
];
const SECTION_BACKGROUND_KEYS = new Set(SECTION_BACKGROUNDS.map(s => s.key));
const BG_DEFAULT_OPACITY = 100;

// Management-only sections — rendered only on siteType 'management' websites,
// so their rows are hidden for artist profiles (both backgrounds and text sizes).
const MANAGEMENT_ONLY_SECTION_KEYS = new Set(['services', 'artistRoster', 'testimonials', 'clients', 'team']);

// Pseudo-target for the "Apply to all" bulk row in the Backgrounds panel.
const BG_ALL_SECTIONS = '__all__';

interface SectionBgEntry { image?: string; video?: string; opacity: number; }
type SectionBgMap = Record<string, SectionBgEntry>;

/**
 * Normalise saved per-section backgrounds into a clean map.
 * Entries carry an image and/or a hero-only video. The hero entry falls back
 * to the legacy single customBackgroundImage, with its opacity taken from
 * --bg-image-opacity (the hero's live opacity source).
 */
function normalizeSectionBackgrounds(
  raw: Record<string, { image?: string | null; video?: string | null; opacity?: number }> | undefined,
  legacyImage: string | null,
  savedFonts: Record<string, string>,
): SectionBgMap {
  const out: SectionBgMap = {};
  for (const key of SECTION_BACKGROUND_KEYS) {
    const entry = raw?.[key];
    const image = typeof entry?.image === 'string' && entry.image ? entry.image : undefined;
    const video = typeof entry?.video === 'string' && entry.video ? entry.video : undefined;
    if (!image && !video) continue;
    const opacity = Number.isFinite(Number(entry?.opacity))
      ? Math.min(100, Math.max(0, Math.round(Number(entry!.opacity))))
      : BG_DEFAULT_OPACITY;
    out[key] = { ...(image ? { image } : {}), ...(video ? { video } : {}), opacity };
  }
  const heroOpacityRaw = savedFonts['--bg-image-opacity'];
  const heroOpacity = heroOpacityRaw != null && heroOpacityRaw !== ''
    ? parseInt(heroOpacityRaw, 10)
    : null;
  if (!out.hero && legacyImage) {
    out.hero = { image: legacyImage, opacity: heroOpacity ?? BG_DEFAULT_OPACITY };
  } else if (out.hero && heroOpacity != null) {
    out.hero = { ...out.hero, opacity: heroOpacity };
  }
  return out;
}

/** Read a stored multiplier (0.5–1.5) as a percentage, or null when unset. */
function readSizePct(
  sections: Record<string, Record<string, string>>,
  sectionKey: string,
  roleKey: string,
): number | null {
  const raw = sections[sectionKey]?.[roleKey];
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * Drop legacy 'all' master entries (site-wide / whole-section sliders were
 * retired in favour of per-role sliders). Stripping at load means the next
 * save cleans them out of storage for good.
 */
function stripMasterSizes(
  sections: Record<string, Record<string, string>>,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [sectionKey, roles] of Object.entries(sections || {})) {
    if (sectionKey === 'all') continue;
    const cleanRoles: Record<string, string> = {};
    for (const [roleKey, value] of Object.entries(roles || {})) {
      if (roleKey === 'all' || roleKey === 'button') continue; // retired keys
      cleanRoles[roleKey] = value;
    }
    if (Object.keys(cleanRoles).length > 0) out[sectionKey] = cleanRoles;
  }
  return out;
}

// ── Colour conversion helpers ────────────────────────────────────────────────
function hexToHsl(hex: string): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    if (import.meta.env.DEV) console.warn(`[ThemeCustomizer] Invalid hex color: ${hex}`);
    return '0 0% 50%';
  }
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function hslToHex(hsl: string): string {
  const m = hsl.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
  if (!m) {
    if (import.meta.env.DEV) console.warn(`[ThemeCustomizer] Invalid HSL color: ${hsl}`);
    return '#888888';
  }
  const h = parseInt(m[1]) / 360;
  const s = parseInt(m[2]) / 100;
  const l = parseInt(m[3]) / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Auto-derive gradient mid/end colours from a start HSL value.
 * Mid: +20° hue shift, -5% lightness
 * End: +40° hue shift, -12% lightness
 * Returns { mid: "H S% L%", end: "H S% L%" } in the same "H S% L%" format.
 */
function deriveGradientColors(startHsl: string): { mid: string; end: string } {
  const m = startHsl.match(/^(\d+)\s+(\d+)%\s+(\d+)%$/);
  if (!m) return { mid: startHsl, end: startHsl };
  const h = parseInt(m[1]);
  const s = parseInt(m[2]);
  const l = parseInt(m[3]);
  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
  const midH = (h + 20) % 360;
  const midL = clamp(l - 5, 5, 95);
  const endH = (h + 40) % 360;
  const endL = clamp(l - 12, 5, 95);
  return {
    mid: `${midH} ${s}% ${midL}%`,
    end: `${endH} ${s}% ${endL}%`,
  };
}

// ── GradientColorPicker — reusable simplified/advanced gradient picker ──────
interface GradientColorPickerProps {
  gradientKeys: { key: string; label: string }[];
  currentColors: Record<string, string>;
  themeDefaults: Record<string, string>;
  onColorChange: (colorKey: string, hex: string) => void;
  onClearColor: (colorKey: string) => void;
  onGradientChange: (startHex: string, startKey: string) => void;
}

const GradientColorPicker: React.FC<GradientColorPickerProps> = ({
  gradientKeys, currentColors, themeDefaults,
  onColorChange, onClearColor, onGradientChange,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const startKey = gradientKeys[0].key;

  const hexColor = currentColors[startKey] ? hslToHex(currentColors[startKey]) : '';
  const defaultHex = themeDefaults[startKey] ? hslToHex(themeDefaults[startKey]) : '';
  const displayHex = hexColor || defaultHex;
  const midKey = gradientKeys[1]?.key;
  const endKey = gradientKeys[2]?.key;
  const startHsl = currentColors[startKey] || themeDefaults[startKey] || '0 0% 50%';
  const derived = deriveGradientColors(startHsl);
  const midHsl = (midKey && (currentColors[midKey] || themeDefaults[midKey])) || derived.mid;
  const endHsl = (endKey && (currentColors[endKey] || themeDefaults[endKey])) || derived.end;
  const previewGradient = `linear-gradient(135deg, ${hslToHex(startHsl)}, ${hslToHex(midHsl)}, ${hslToHex(endHsl)})`;

  if (!advanced) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <label className="flex-1 flex items-center gap-3 cursor-pointer group rounded-lg border border-border hover:border-primary/40 px-3 py-2.5 transition-colors bg-card">
            <span
              className="block w-8 h-8 rounded-md border-2 shrink-0 shadow-sm transition-all"
              style={{
                background: previewGradient,
                borderColor: hexColor ? 'hsl(var(--primary))' : 'hsl(var(--border))',
              }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium leading-tight">Gradient</p>
              <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">
                {hexColor || (defaultHex ? `${defaultHex} (default)` : 'pick a colour')}
              </p>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
              <Pipette className="w-3.5 h-3.5" />
            </div>
            <input type="color" className="sr-only" value={displayHex || '#888888'} onChange={e => onGradientChange(e.target.value, startKey)} />
          </label>
          {hexColor && (
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 rounded"
              onClick={() => onClearColor(startKey)}
              title="Reset to theme default"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          onClick={() => setAdvanced(true)}
        >
          <ChevronDown className="w-3 h-3" /> Advanced
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {gradientKeys.map(ck => {
          const hexColor = currentColors[ck.key] ? hslToHex(currentColors[ck.key]) : '';
          const defaultHex = themeDefaults[ck.key] ? hslToHex(themeDefaults[ck.key]) : '';
          const displayHex = hexColor || defaultHex;
          return (
            <div key={ck.key} className="space-y-1">
              <label className="block cursor-pointer group">
                <div
                  className={`relative h-10 rounded-lg border-2 overflow-hidden transition-all ${
                    hexColor ? 'border-primary' : 'border-border group-hover:border-muted-foreground/50'
                  }`}
                  style={{ background: displayHex || '#888' }}
                >
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
                    <Pipette className="w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                  </div>
                  <input type="color" className="sr-only" value={displayHex || '#888888'} onChange={e => onColorChange(ck.key, e.target.value)} />
                </div>
                <p className="text-[10px] text-center text-muted-foreground mt-0.5">{ck.label}</p>
              </label>
              {hexColor && (
                <button
                  type="button"
                  className="w-full text-[10px] text-muted-foreground hover:text-destructive flex items-center justify-center gap-0.5 transition-colors"
                  onClick={() => onClearColor(ck.key)}
                >
                  <X className="w-2.5 h-2.5" /> reset
                </button>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
        onClick={() => setAdvanced(false)}
      >
        <ChevronUp className="w-3 h-3" /> Simple mode
      </button>
    </div>
  );
};

// ── Google Fonts dynamic loader (for in-picker preview) ─────────────────────
const PREVIEW_FONT_LINK_ATTR = 'data-preview-font';
const _loadedBatches = new Set<string>();
function loadFontsForPreview(fontNames: string[]) {
  if (!fontNames.length) return;
  for (let i = 0; i < fontNames.length; i += 40) {
    const batch = [...fontNames.slice(i, i + 40)].sort();
    const key = batch.join('|');
    if (_loadedBatches.has(key)) continue;
    _loadedBatches.add(key);
    const params = batch.map(f => `family=${encodeURIComponent(f)}`).join('&');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.setAttribute(PREVIEW_FONT_LINK_ATTR, key);
    link.href = `https://fonts.googleapis.com/css2?${params}&display=swap`;
    document.head.appendChild(link);
  }
}
function clearPreviewFontLinks() {
  document.querySelectorAll(`link[${PREVIEW_FONT_LINK_ATTR}]`).forEach(el => el.remove());
  _loadedBatches.clear();
}

// ── FontPickerCard ────────────────────────────────────────────────────────────
interface FontPickerCardProps {
  role: FontRoleDef;
  currentFont: string;
  currentColors: Record<string, string>;
  currentWeight: string;
  fontList: string[];
  fontsLoading: boolean;
  themeDefaults: Record<string, string>;
  onFontChange: (v: string) => void;
  onColorChange: (colorKey: string, hex: string) => void;
  onClearColor: (colorKey: string) => void;
  onWeightChange: (weightKey: string, w: string | null) => void;
  onGradientChange?: (startHex: string, startKey: string) => void;
}

const FontPickerCard: React.FC<FontPickerCardProps> = ({
  role, currentFont, currentColors, currentWeight,
  fontList, fontsLoading, themeDefaults,
  onFontChange, onColorChange, onClearColor, onWeightChange,
  onGradientChange,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [hoverFont, setHoverFont] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [advancedGradient, setAdvancedGradient] = useState(false);

  // Theme default option is always the first item
  const THEME_DEFAULT_ID = '__theme_default__';

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);
  const filtered = useMemo(() =>
    debouncedSearch.trim()
      ? fontList.filter(f => f.toLowerCase().includes(debouncedSearch.toLowerCase()))
      : fontList,
  [fontList, debouncedSearch]);

  // Virtualized list config
  const ITEM_HEIGHT = 40; // matches py-2.5 + text line
  const CONTAINER_HEIGHT = 240;
  const BUFFER = 8; // extra items rendered above/below viewport

  // Build full items list: theme default + filtered fonts
  const allItems = useMemo(() => {
    return [THEME_DEFAULT_ID, ...filtered];
  }, [filtered]);

  const totalHeight = allItems.length * ITEM_HEIGHT;

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER);
  const endIndex = Math.min(
    allItems.length,
    Math.ceil((scrollTop + CONTAINER_HEIGHT) / ITEM_HEIGHT) + BUFFER
  );

  // Parse theme-default font name from CSS var value like "'Playfair Display', serif"
  const defaultFontName = (themeDefaults[role.fontKey] || '')
    .split(',')[0].replace(/['"]/g, '').trim();

  const displayFont = hoverFont ?? currentFont;

  useEffect(() => {
    if (open) loadFontsForPreview(filtered.slice(0, 80));
  }, [open, filtered.length, filtered[0]]);

  useEffect(() => {
    if (currentFont) loadFontsForPreview([currentFont]);
  }, [currentFont]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const dropId = `font-drop-${role.fontKey}`;
    const handler = (e: MouseEvent) => {
      const drop = document.getElementById(dropId);
      if (!triggerRef.current?.contains(e.target as Node) && !drop?.contains(e.target as Node)) {
        setOpen(false);
        setHoverFont(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reposition dropdown on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setDropdownStyle({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width, zIndex: 9999 });
      }
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const openDropdown = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({ position: 'fixed', top: r.bottom + 4, left: r.left, width: r.width, zIndex: 9999 });
    }
    setOpen(true);
    setScrollTop(0);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const selectFont = (font: string) => {
    onFontChange(font);
    setSearch('');
    setOpen(false);
    setHoverFont(null);
  };

  // Render a single virtualized row
  const renderItem = (itemId: string, index: number) => {
    const top = index * ITEM_HEIGHT;
    if (itemId === THEME_DEFAULT_ID) {
      return (
        <div
          key={THEME_DEFAULT_ID}
          className={`absolute left-0 right-0 flex items-center justify-between px-3 cursor-pointer hover:bg-muted/60 border-b border-border/40 ${!currentFont ? 'bg-primary/10' : ''}`}
          style={{ top, height: ITEM_HEIGHT }}
          onMouseDown={e => { e.preventDefault(); selectFont(''); }}
          onMouseEnter={() => {
            if (defaultFontName) loadFontsForPreview([defaultFontName]);
            setHoverFont(defaultFontName || null);
          }}
          onMouseLeave={() => setHoverFont(null)}
        >
          <span className="text-sm text-muted-foreground italic">
            {defaultFontName ? `${defaultFontName} (default)` : 'Theme default'}
          </span>
          {!currentFont && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
        </div>
      );
    }

    return (
      <div
        key={itemId}
        className={`absolute left-0 right-0 flex items-center justify-between px-3 cursor-pointer hover:bg-muted/60 transition-colors ${currentFont === itemId ? 'bg-primary/10' : ''}`}
        style={{ top, height: ITEM_HEIGHT }}
        onMouseDown={e => { e.preventDefault(); selectFont(itemId); }}
        onMouseEnter={() => {
          loadFontsForPreview([itemId]);
          setHoverFont(itemId);
        }}
        onMouseLeave={() => setHoverFont(null)}
      >
        <span className="text-sm leading-tight truncate" style={{ fontFamily: `'${itemId}', sans-serif` }}>{itemId}</span>
        {currentFont === itemId && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Preview strip — always visible; shows hovered / selected / theme-default font */}
      <div
        className="px-3 py-2.5 rounded-lg bg-background border border-border/50 min-h-13 flex flex-col justify-center overflow-hidden"
        style={{ fontFamily: `'${displayFont || defaultFontName}', sans-serif` }}
      >
        <p className="font-semibold text-sm truncate">
          {displayFont || defaultFontName || 'Theme default'}
          {hoverFont && hoverFont !== currentFont && (
            <span className="text-muted-foreground font-normal text-xs ml-1.5 italic">preview</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground truncate opacity-70">The quick brown fox jumps over the lazy dog</p>
      </div>

      {/* Font selector trigger */}
      <div>
        <div
          ref={triggerRef}
          className={`flex items-center gap-2 h-9 w-full rounded-lg border bg-card px-3 text-sm cursor-pointer select-none transition-all ${
            open ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-muted-foreground/40'
          }`}
          onClick={openDropdown}
        >
          <Type className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          {open ? (
            <input
              ref={inputRef}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
              placeholder="Search 1,900+ Google Fonts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="flex-1 truncate" style={currentFont ? { fontFamily: `'${currentFont}', sans-serif` } : {}}>
              {currentFont
                ? currentFont
                : <span className="text-muted-foreground italic text-xs">{defaultFontName ? `${defaultFontName} (default)` : 'Theme default'}</span>
              }
            </span>
          )}
          {open
            ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          }
        </div>
        {currentFont && !open && (
          <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 transition-colors" onClick={() => onFontChange('')}>
            <X className="w-3 h-3" /> Reset to theme default
          </button>
        )}
      </div>

      {/* Dropdown — rendered via portal so it escapes overflow:hidden parents */}
      {open && createPortal(
        <div
          id={`font-drop-${role.fontKey}`}
          className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
          style={dropdownStyle}
        >
          {fontsLoading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
              <Spinner size="xs" /> Loading fonts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-5 text-center text-sm text-muted-foreground">No fonts match &quot;{search}&quot;</div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="overflow-y-auto"
              style={{ maxHeight: CONTAINER_HEIGHT }}
              onScroll={e => setScrollTop((e.target as HTMLDivElement).scrollTop)}
            >
              {/* Virtual spacer maintains correct scrollbar height */}
              <div style={{ height: totalHeight, position: 'relative' }}>
                {allItems.slice(startIndex, endIndex).map((item, i) =>
                  renderItem(item, startIndex + i)
                )}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Colour swatches — simplified gradient for heading, full grid for others */}
      <div>
        <p className="text-[11px] font-medium text-muted-foreground mb-2">
          {role.isGradient ? 'Gradient colour' : role.colorKeys.length > 1 ? 'Gradient colours' : 'Text colour'}
        </p>

        {role.isGradient && !advancedGradient ? (
          /* ── Simplified gradient: single colour picker that auto-derives mid/end ── */
          <div className="space-y-2">
            {(() => {
              const startKey = role.colorKeys[0].key;
              const hexColor = currentColors[startKey] ? hslToHex(currentColors[startKey]) : '';
              const defaultHex = themeDefaults[startKey] ? hslToHex(themeDefaults[startKey]) : '';
              const displayHex = hexColor || defaultHex;
              const midKey = role.colorKeys[1]?.key;
              const endKey = role.colorKeys[2]?.key;
              const startHsl = currentColors[startKey] || themeDefaults[startKey] || '0 0% 50%';
              const derived = deriveGradientColors(startHsl);
              const midHsl = (midKey && (currentColors[midKey] || themeDefaults[midKey])) || derived.mid;
              const endHsl = (endKey && (currentColors[endKey] || themeDefaults[endKey])) || derived.end;
              const previewGradient = `linear-gradient(135deg, ${hslToHex(startHsl)}, ${hslToHex(midHsl)}, ${hslToHex(endHsl)})`;
              return (
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center gap-3 cursor-pointer group rounded-lg border border-border hover:border-primary/40 px-3 py-2.5 transition-colors bg-card">
                    <span
                      className="block w-8 h-8 rounded-md border-2 shrink-0 shadow-sm transition-all"
                      style={{
                        background: previewGradient,
                        borderColor: hexColor ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium leading-tight">Gradient</p>
                      <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">
                        {hexColor || (defaultHex ? `${defaultHex} (default)` : 'pick a colour…')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
                      <Pipette className="w-3.5 h-3.5" />
                    </div>
                    <input type="color" className="sr-only" value={displayHex || '#888888'} onChange={e => onGradientChange?.(e.target.value, startKey)} />
                  </label>
                  {hexColor && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 rounded"
                      onClick={() => onClearColor(startKey)}
                      title="Reset to theme default"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })()}
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              onClick={() => setAdvancedGradient(true)}
            >
              <ChevronDown className="w-3 h-3" /> Advanced
            </button>
          </div>
        ) : (
          /* ── Full colour grid (existing layout) ── */
          <>
            <div className={`grid gap-2 ${role.colorKeys.length > 1 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {role.colorKeys.map(ck => {
                const hexColor = currentColors[ck.key] ? hslToHex(currentColors[ck.key]) : '';
                const defaultHex = themeDefaults[ck.key] ? hslToHex(themeDefaults[ck.key]) : '';
                const displayHex = hexColor || defaultHex;
                return (
                  <div key={ck.key} className="space-y-1">
                    <label className="block cursor-pointer group">
                      <div
                        className={`relative h-10 rounded-lg border-2 overflow-hidden transition-all ${
                          hexColor ? 'border-primary' : 'border-border group-hover:border-muted-foreground/50'
                        }`}
                        style={{ background: displayHex || '#888' }}
                      >
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
                          <Pipette className="w-3.5 h-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                        </div>
                        <input type="color" className="sr-only" value={displayHex || '#888888'} onChange={e => onColorChange(ck.key, e.target.value)} />
                      </div>
                      <p className="text-[10px] text-center text-muted-foreground mt-0.5">{ck.label}</p>
                    </label>
                    {hexColor && (
                      <button
                        type="button"
                        className="w-full text-[10px] text-muted-foreground hover:text-destructive flex items-center justify-center gap-0.5 transition-colors"
                        onClick={() => onClearColor(ck.key)}
                      >
                        <X className="w-2.5 h-2.5" /> reset
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {role.isGradient && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 mt-2 transition-colors"
                onClick={() => setAdvancedGradient(false)}
              >
                <ChevronUp className="w-3 h-3" /> Simple mode
              </button>
            )}
          </>
        )}
      </div>

      {/* Font weight pills — all roles */}
      <div>
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
          Font weight
          {themeDefaults[role.weightKey] && (
            <span className="ml-1 opacity-50">(theme: {themeDefaults[role.weightKey]})</span>
          )}
        </p>
        <div className="flex gap-1.5 flex-wrap">
          {FONT_WEIGHTS.map(w => (
            <button
              key={w}
              type="button"
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                currentWeight === w
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:border-primary/50 text-muted-foreground'
              }`}
              onClick={() => onWeightChange(role.weightKey, currentWeight === w ? null : w)}
            >{w}</button>
          ))}
          {currentWeight && (
            <button
              type="button"
              className="text-[10px] text-muted-foreground hover:text-foreground ml-0.5 flex items-center gap-0.5 transition-colors"
              onClick={() => onWeightChange(role.weightKey, null)}
            >
              <X className="w-3 h-3" /> clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── SizeSliderRow — one 50–150% scale slider with clear button ───────────────
interface SizeSliderRowProps {
  label: string;
  value: number | null; // percentage (50–150) or null = theme default
  onChange: (pct: number | null) => void;
  muted?: boolean;
}

const SizeSliderRow: React.FC<SizeSliderRowProps> = ({ label, value, onChange, muted }) => {
  const pct = value ?? 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className={`text-[11px] font-medium ${muted ? 'text-muted-foreground' : ''}`}>{label}</p>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-mono text-muted-foreground">
            {value != null ? `${value}%` : '100%'}
          </span>
          {value != null && (
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded"
              onClick={() => onChange(null)}
              title="Reset to theme default"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        min={SIZE_MIN}
        max={SIZE_MAX}
        step={5}
        value={pct}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none bg-border cursor-pointer accent-primary"
      />
    </div>
  );
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface ThemeCustomizerDrawerProps {
  open: boolean;
  onClose: () => void;
  userThemeId: string;
  themeTitle: string;
  /** True when the theme being customised is already a variant (not a base theme) */
  isVariant?: boolean;
  /** The master theme's CSS title (e.g. "Fire") — used to load default CSS vars */
  masterThemeTitle?: string;
  siteName?: string;
  savedFonts?: Record<string, string>;
  savedColors?: Record<string, string>;
  savedFontWeight?: number | null; // legacy compat — weights now live in savedFonts
  savedBackgroundImage?: string | null;
  /** Per-section / per-role font-size scale multipliers: { [section]: { [role]: '1.2' } } */
  savedSections?: Record<string, Record<string, string>>;
  /** Per-section background overrides: { [section]: { image?, video?, opacity } } (video: hero only) */
  savedSectionBackgrounds?: Record<string, { image?: string | null; video?: string | null; opacity?: number }>;
  /** 'artist' (default) | 'management' — management-only sections are hidden on artist sites. */
  siteType?: 'artist' | 'management';
  onSaved?: () => void;
}

// ── Main Drawer ───────────────────────────────────────────────────────────────
const ThemeCustomizerDrawer: React.FC<ThemeCustomizerDrawerProps> = ({
  open,
  onClose,
  userThemeId,
  themeTitle,
  isVariant = false,
  masterThemeTitle,
  siteName = '',
  savedFonts = {},
  savedColors = {},
  savedBackgroundImage = null,
  savedSections: rawSavedSections = {},
  savedSectionBackgrounds: rawSavedSectionBackgrounds = {},
  siteType = 'artist',
  onSaved,
}) => {
  // Legacy 'all' master keys are retired — strip them so they can't linger
  // invisibly (stripped values also get cleaned from storage on next save).
  const savedSections = useMemo(() => stripMasterSizes(rawSavedSections), [rawSavedSections]);
  const savedSectionBackgrounds = useMemo(
    () => normalizeSectionBackgrounds(rawSavedSectionBackgrounds, savedBackgroundImage, savedFonts),
    [rawSavedSectionBackgrounds, savedBackgroundImage, savedFonts],
  );

  // Management-only sections (roster/testimonials/clients/team) render only on
  // siteType 'management' websites — hide their rows for artist profiles.
  const isManagementSite = siteType === 'management';
  const visibleBgSections = SECTION_BACKGROUNDS.filter(s => isManagementSite || !MANAGEMENT_ONLY_SECTION_KEYS.has(s.key));
  const visibleSizeSections = SIZE_SECTIONS.filter(s => isManagementSite || !MANAGEMENT_ONLY_SECTION_KEYS.has(s.key));
  const previewContext = useContext(PreviewContext);

  // Close drawer on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // fonts holds both font-family keys AND weight keys
  // e.g. { '--font-heading': 'Playfair Display', '--font-heading-weight': '700' }
  const [fonts, setFonts] = useState<Record<string, string>>(savedFonts);
  const [colors, setColors] = useState<Record<string, string>>(savedColors);
  const [customSections, setCustomSections] = useState<Record<string, Record<string, string>>>(savedSections);
  const [openSizeSection, setOpenSizeSection] = useState<string | null>(null);
  const [textSizesOpen, setTextSizesOpen] = useState(false);
  const [sectionBackgrounds, setSectionBackgrounds] = useState<SectionBgMap>(savedSectionBackgrounds);
  const [openBgSection, setOpenBgSection] = useState<string | null>(null);
  // Ref (not state) so upload handlers always see the row that opened them.
  const bgTargetRef = useRef<string>('hero');
  const [allBgOpacity, setAllBgOpacity] = useState<number>(BG_DEFAULT_OPACITY);
  const [bgUploadStep, setBgUploadStep] = useState<'idle' | 'cropping'>('idle');
  const [bgSelectedFile, setBgSelectedFile] = useState<File | null>(null);
  const [bgUploading, setBgUploading] = useState(false);
  // Real-time upload progress: null = idle, number = 0–100 while bytes transfer.
  const [bgUploadStatus, setBgUploadStatus] = useState<{ label: string; progress: number | null } | null>(null);
  const bgCropperRef = useRef<any>(null);
  const bgFileInputRef = useRef<HTMLInputElement>(null);

  // Hero background is just the 'hero' entry of the map. On save it is mirrored
  // into the legacy customBackgroundImage / --bg-image-opacity fields.
  const backgroundImage = sectionBackgrounds.hero?.image ?? null;
  const bgImageOpacity = sectionBackgrounds.hero?.opacity ?? BG_DEFAULT_OPACITY;

  const [fontList, setFontList] = useState<string[]>([]);
  const [fontsLoading, setFontsLoading] = useState(false);
  const [themeDefaults, setThemeDefaults] = useState<Record<string, string>>({});

  const [openSection, setOpenSection] = useState<'fonts' | 'colors' | 'backgrounds' | 'buttons' | null>('fonts');
  const [openTypographyCard, setOpenTypographyCard] = useState<string | null>('heading');

  // Track the "live" theme after a fork
  const [currentId, setCurrentId] = useState(userThemeId);
  const [currentTitle, setCurrentTitle] = useState(themeTitle);
  const [hasForkedInSession, setHasForkedInSession] = useState(false);

  // Fork name input state
  const [forkOpen, setForkOpen] = useState(false);
  const [forkName, setForkName] = useState('');
  const [forking, setForking] = useState(false);

  // Dirty tracking: JSON snapshot of last-saved {fonts, colors}
  const lastSavedSnapshotRef = useRef<string | null>(null);
  // Track last-saved fonts/colors so theme-preview-reset can restore them instantly
  // without waiting for the socket → refetch cycle (avoids stale-data flash on close)
  const lastSavedFontsRef = useRef<Record<string, string>>(savedFonts);
  const lastSavedColorsRef = useRef<Record<string, string>>(savedColors);
  const lastSavedMasterTitleRef = useRef<string>(masterThemeTitle || themeTitle);
  const lastSavedBgImageRef = useRef<string | null>(savedBackgroundImage);
  const lastSavedSectionsRef = useRef<Record<string, Record<string, string>>>(savedSections);
  const lastSavedBackgroundsRef = useRef<SectionBgMap>(savedSectionBackgrounds);
  const currentSnapshot = useMemo(() => JSON.stringify({ fonts, colors, customSections, sectionBackgrounds }), [fonts, colors, customSections, sectionBackgrounds]);
  const isCurrentVariant = isVariant || hasForkedInSession;
  const hasChanges = lastSavedSnapshotRef.current !== null && lastSavedSnapshotRef.current !== currentSnapshot;
  // After first save, auto-detect variant for subsequent saves
  const canSave = isCurrentVariant || hasForkedInSession;

  // Pre-fill fork name — always use base themeTitle prop to avoid "X by Y by Y by Y"
  useEffect(() => {
    if (forkOpen) {
      setForkName(siteName ? `${themeTitle} by ${siteName}` : `${themeTitle} Custom`);
    }
  }, [forkOpen]);

  // Sync when drawer opens or when the theme identity/saved customizations change;
  // reset preview when it closes.
  useEffect(() => {
    if (open) {
      clearPreviewFontLinks();
      setFonts(savedFonts);
      setColors(savedColors);
      setCustomSections(savedSections);
      setSectionBackgrounds(savedSectionBackgrounds);
      setOpenBgSection(null);
      bgTargetRef.current = 'hero';
      setAllBgOpacity(Object.values(savedSectionBackgrounds)[0]?.opacity ?? BG_DEFAULT_OPACITY);
      setCurrentId(userThemeId);
      setCurrentTitle(themeTitle);
      setHasForkedInSession(false);
      setForkOpen(false);
      setForkName('');
      setBgUploadStep('idle');
      setBgSelectedFile(null);
      setOpenTypographyCard('heading');
      setOpenSizeSection(null);
      setTextSizesOpen(false);
      // Initialize snapshot to the saved state so hasChanges detects deviations
      lastSavedSnapshotRef.current = JSON.stringify({ fonts: savedFonts, colors: savedColors, customSections: savedSections, sectionBackgrounds: savedSectionBackgrounds });
      lastSavedFontsRef.current = savedFonts;
      lastSavedColorsRef.current = savedColors;
      lastSavedMasterTitleRef.current = masterThemeTitle || themeTitle;
      lastSavedBgImageRef.current = savedBackgroundImage;
      lastSavedSectionsRef.current = savedSections;
      lastSavedBackgroundsRef.current = savedSectionBackgrounds;

      // Open the preview panel, then send initial preview with any saved customizations
      previewContext?.triggerOpenPanel();
      const t = setTimeout(() => {
        const iframe = document.querySelector<HTMLIFrameElement>('iframe[title="Website Preview"]');
        iframe?.contentWindow?.postMessage(
          { type: 'theme-preview', theme: masterThemeTitle || themeTitle, customFonts: savedFonts, customColors: savedColors, customFontWeight: null, customBackgroundImage: savedSectionBackgrounds.hero?.image ?? null, customSectionBackgrounds: savedSectionBackgrounds, customSections: savedSections },
          '*',
        );
      }, 350);
      return () => clearTimeout(t);
    } else {
      // Drawer closed — tell the iframe to revert to the active theme.
      // Pass the last-saved fonts/colors so the iframe restores them immediately
      // without waiting for the socket → refetch cycle (avoids stale-data flash).
      const iframe = document.querySelector<HTMLIFrameElement>('iframe[title="Website Preview"]');
      iframe?.contentWindow?.postMessage({
        type: 'theme-preview-reset',
        savedFonts: lastSavedFontsRef.current,
        savedColors: lastSavedColorsRef.current,
        masterThemeTitle: lastSavedMasterTitleRef.current,
        savedBackgroundImage: lastSavedBgImageRef.current,
        savedSectionBackgrounds: lastSavedBackgroundsRef.current,
        savedSections: lastSavedSectionsRef.current,
      }, '*');
      clearPreviewFontLinks();
    }
  }, [open, userThemeId, themeTitle, masterThemeTitle, savedBackgroundImage, JSON.stringify(savedFonts), JSON.stringify(savedColors), JSON.stringify(rawSavedSectionBackgrounds)]);

  // Fetch theme default CSS vars from the backend (parsed from artist-website/src/index.css).
  // This is reliable regardless of whether the preview iframe is loaded or deployed.
  // Keep previous defaults during refetch to avoid white swatch flash.
  useEffect(() => {
    if (!open) return;
    const title = masterThemeTitle || themeTitle;
    axios.get(`${BASE_URL}/api/user-theme/theme-defaults`, { params: { masterTitle: title } })
      .then(res => {
        if (res.data?.vars && Object.keys(res.data.vars).length > 0) {
          setThemeDefaults(res.data.vars);
        }
      })
      .catch(() => { /* silently ignore — defaults stay empty */ });
  }, [open, masterThemeTitle, themeTitle]);

  // Load font list once per drawer session, with a single retry on empty result
  const fontRetryRef = useRef(false);
  useEffect(() => {
    if (!open || fontList.length > 0) return;
    setFontsLoading(true);
    fontRetryRef.current = false;
    axios.get(`${BASE_URL}/api/user-theme/fonts`)
      .then(res => {
        const list: string[] = res.data.fonts || [];
        if (list.length > 0) {
          setFontList(list);
          const selected = FONT_ROLES.map(r => savedFonts[r.fontKey]).filter(Boolean);
          if (selected.length) loadFontsForPreview(selected);
          setFontsLoading(false);
        } else if (!fontRetryRef.current) {
          // Server cache was cold — it may be refetching from Google right now.
          // Retry once after a short delay.
          fontRetryRef.current = true;
          setTimeout(() => {
            axios.get(`${BASE_URL}/api/user-theme/fonts`)
              .then(r2 => {
                const retry: string[] = r2.data.fonts || [];
                setFontList(retry);
                if (retry.length) {
                  const selected = FONT_ROLES.map(r => savedFonts[r.fontKey]).filter(Boolean);
                  if (selected.length) loadFontsForPreview(selected);
                }
              })
              .catch(() => {})
              .finally(() => setFontsLoading(false));
          }, 3000);
        } else {
          setFontsLoading(false);
        }
      })
      .catch(() => { toast.error('Could not load font list'); setFontsLoading(false); });
  }, [open]);

  // ── Debounced live preview ──────────────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendPreview = useCallback((
    nextFonts: Record<string, string>,
    nextColors: Record<string, string>,
    nextBackgrounds?: SectionBgMap,
    nextSections?: Record<string, Record<string, string>>,
  ) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const iframe = document.querySelector<HTMLIFrameElement>('iframe[title="Website Preview"]');
      if (!iframe?.contentWindow) return;
      const bgs = nextBackgrounds ?? sectionBackgrounds;
      const heroImage = bgs.hero?.image ?? null;
      const opacity = bgs.hero?.opacity ?? BG_DEFAULT_OPACITY;
      const fontsWithOpacity = opacity !== 100
        ? { ...nextFonts, '--bg-image-opacity': String(opacity) }
        : (() => { const f = { ...nextFonts }; delete f['--bg-image-opacity']; return f; })();
      iframe.contentWindow.postMessage(
        { type: 'theme-preview', theme: masterThemeTitle || currentTitle, customFonts: fontsWithOpacity, customColors: nextColors, customFontWeight: null, customBackgroundImage: heroImage, customSectionBackgrounds: bgs, customSections: nextSections ?? customSections },
        '*',
      );
    }, 300);
  }, [currentTitle, masterThemeTitle, sectionBackgrounds, customSections]);

  const handleFontChange = (key: string, value: string) => {
    const next = { ...fonts, [key]: value };
    if (!value) delete next[key];
    setFonts(next);
    sendPreview(next, colors);
  };

  const handleColorChange = (key: string, hex: string) => {
    const next = { ...colors, [key]: hexToHsl(hex) };
    setColors(next);
    sendPreview(fonts, next);
  };

  const handleClearColor = (key: string) => {
    const next = { ...colors };
    delete next[key];
    setColors(next);
    sendPreview(fonts, next);
  };

  const handleWeightChange = (weightKey: string, w: string | null) => {
    const next = { ...fonts };
    if (w) next[weightKey] = w; else delete next[weightKey];
    setFonts(next);
    sendPreview(next, colors);
  };

  // ── Section text-size handlers ──
  // pct is 50–150; stored as a unitless multiplier string (0.5–1.5).
  // null clears the key so the next fallback (role → section-all → site-all → theme) applies.
  const handleSizeChange = (sectionKey: string, roleKey: string, pct: number | null) => {
    const next = { ...customSections };
    const section = { ...(next[sectionKey] || {}) };
    if (pct == null) {
      delete section[roleKey];
    } else {
      section[roleKey] = String(Math.min(SIZE_MAX, Math.max(SIZE_MIN, pct)) / 100);
    }
    if (Object.keys(section).length > 0) next[sectionKey] = section;
    else delete next[sectionKey];
    setCustomSections(next);
    sendPreview(fonts, colors, sectionBackgrounds, next);
  };

  // Global Buttons scale — stored in the fonts map, applies to every primary button
  const handleButtonScaleChange = (pct: number | null) => {
    const next = { ...fonts };
    if (pct == null) delete next[BUTTON_SCALE_KEY];
    else next[BUTTON_SCALE_KEY] = String(Math.min(SIZE_MAX, Math.max(SIZE_MIN, pct)) / 100);
    setFonts(next);
    sendPreview(next, colors);
  };
  const buttonScalePct = (() => {
    const raw = fonts[BUTTON_SCALE_KEY];
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  })();

  const handleRadiusMode = (group: RadiusGroupDef, mode: RadiusMode) => {
    const next = { ...fonts };
    if (mode === 'default') delete next[group.key];
    else if (mode === 'rounded') next[group.key] = group.roundedValue;
    else if (mode === 'square') next[group.key] = '0px';
    else next[group.key] = `${getCustomRadius(fonts[group.key], group.defaultCustom)}px`;
    setFonts(next);
    sendPreview(next, colors);
  };

  const handleCustomRadius = (key: RadiusToken, radius: number) => {
    const next = { ...fonts, [key]: `${Math.min(48, Math.max(0, radius))}px` };
    setFonts(next);
    sendPreview(next, colors);
  };

  const getMasterRadiusMode = (): RadiusMode | 'mixed' => {
    const modes = RADIUS_GROUPS.map(group => getRadiusMode(fonts[group.key], group.roundedValue));
    return modes.every(mode => mode === modes[0]) ? modes[0] : 'mixed';
  };

  const handleMasterRadiusMode = (mode: RadiusMode) => {
    const next = { ...fonts };
    for (const group of RADIUS_GROUPS) {
      if (mode === 'default') delete next[group.key];
      else if (mode === 'rounded') next[group.key] = group.roundedValue;
      else if (mode === 'square') next[group.key] = '0px';
      else next[group.key] = `${group.defaultCustom}px`;
    }
    setFonts(next);
    sendPreview(next, colors);
  };

  const handleGradientChange = (startHex: string, startKey: string = '--gradient-from') => {
    const startHsl = hexToHsl(startHex);
    const { mid, end } = deriveGradientColors(startHsl);
    const prefix = startKey.replace(/-from$/, '');
    const nextColors = {
      ...colors,
      [`${prefix}-from`]: startHsl,
      [`${prefix}-mid`]: mid,
      [`${prefix}-to`]: end,
    };
    setColors(nextColors);
    sendPreview(fonts, nextColors);
  };

  // ── Background image/video upload handlers ──
  const validateAndSetBgFile = (file: File) => {
    if (bgUploading) return; // one upload at a time
    const isVideo = file.type.startsWith('video/');
    if (isVideo) {
      // Videos are hero-only for now
      if (bgTargetRef.current !== 'hero') {
        toast.error('Video backgrounds are supported for the hero section only.');
        return;
      }
      if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) {
        toast.error('Only MP4, WebM, and MOV videos are allowed.');
        return;
      }
      if (file.size > 30 * 1024 * 1024) {
        toast.error('Video size must be under 30 MB.');
        return;
      }
      uploadBgVideo(file);
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Only PNG, JPEG, and WebP files are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5 MB.');
      return;
    }
    setBgSelectedFile(file);
    setBgUploadStep('cropping');
  };

  // Hero background video — uploaded as-is (no cropping) to the video endpoint,
  // which can use a dedicated Cloudinary account (see backend utils/cloudinaryMedia.js).
  // Real-time byte-level upload progress for the shared progress bar.
  const makeBgProgressTracker = (uploadingLabel: string) => (e: { loaded: number; total?: number }) => {
    const pct = e.total ? Math.min(100, Math.round((e.loaded / e.total) * 100)) : 0;
    setBgUploadStatus(pct >= 100
      ? { label: 'Processing…', progress: 100 }
      : { label: uploadingLabel, progress: pct });
  };

  const uploadBgVideo = async (file: File) => {
    setBgUploading(true);
    setBgUploadStatus({ label: 'Uploading video…', progress: 0 });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${BASE_URL}/api/videos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: makeBgProgressTracker('Uploading video…'),
      });
      const url = uploadRes.data?.url;
      if (url) {
        const next = {
          ...sectionBackgrounds,
          [bgTargetRef.current]: { video: url, opacity: sectionBackgrounds[bgTargetRef.current]?.opacity ?? BG_DEFAULT_OPACITY },
        };
        setSectionBackgrounds(next);
        sendPreview(fonts, colors, next);
        toast.success('Hero background video uploaded');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to upload video');
    } finally {
      setBgUploading(false);
      setBgUploadStatus(null);
    }
  };

  const handleBgFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetBgFile(file);
    if (bgFileInputRef.current) bgFileInputRef.current.value = '';
  };

  const handleBgDrop = (e: React.DragEvent<HTMLDivElement>, sectionKey: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-muted/50');
    bgTargetRef.current = sectionKey;
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetBgFile(file);
  };

  const handleBgCropComplete = async (croppedImage: string) => {
    setBgUploading(true);
    setBgUploadStatus({ label: 'Processing image…', progress: null });
    try {
      // Convert data URL to blob
      const res = await fetch(croppedImage);
      const blob = await res.blob();
      const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
      const formData = new FormData();
      formData.append('file', blob, `background.${ext}`);
      const uploadRes = await axios.post(`${BASE_URL}/api/images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: makeBgProgressTracker('Uploading image…'),
      });
      const url = uploadRes.data?.url;
      if (url) {
        const target = bgTargetRef.current;
        const next = target === BG_ALL_SECTIONS
          ? (() => {
              const all = { ...sectionBackgrounds };
              for (const section of visibleBgSections) {
                all[section.key] = { image: url, opacity: allBgOpacity };
              }
              return all;
            })()
          : {
              ...sectionBackgrounds,
              [target]: { image: url, opacity: sectionBackgrounds[target]?.opacity ?? BG_DEFAULT_OPACITY },
            };
        setSectionBackgrounds(next);
        sendPreview(fonts, colors, next);
        toast.success(target === BG_ALL_SECTIONS ? 'Background applied to all sections' : 'Background image uploaded');
      }
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setBgUploading(false);
      setBgUploadStatus(null);
      setBgUploadStep('idle');
      setBgSelectedFile(null);
    }
  };

  const openBgFileUpload = (sectionKey: string) => {
    // Guard against double-trigger (Card click + rapid re-click) and concurrent uploads
    if (bgUploadStep !== 'idle' || bgUploading) return;
    bgTargetRef.current = sectionKey;
    bgFileInputRef.current?.click();
  };

  const resetBgUpload = () => {
    setBgUploadStep('idle');
    setBgSelectedFile(null);
  };

  // Remove a section's custom background image — form only, no DB write
  const handleRemoveBg = (sectionKey: string) => {
    const next = { ...sectionBackgrounds };
    delete next[sectionKey];
    setSectionBackgrounds(next);
    sendPreview(fonts, colors, next);
  };

  // Bulk opacity — adjusts every section that has a background image
  const handleAllOpacityChange = (value: number) => {
    setAllBgOpacity(value);
    const next: SectionBgMap = {};
    for (const [key, entry] of Object.entries(sectionBackgrounds)) {
      next[key] = { ...entry, opacity: value };
    }
    setSectionBackgrounds(next);
    sendPreview(fonts, colors, next);
  };

  // Clear every section's custom background at once
  const handleClearAllBackgrounds = () => {
    setSectionBackgrounds({});
    sendPreview(fonts, colors, {});
  };

  // ── Fork: base theme → create named variant & activate ──────────────────────
  const handleFork = async () => {
    const name = forkName.trim();
    if (!name) { toast.error('Enter a name for your custom theme'); return; }
    setForking(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/user-theme/${currentId}/fork`, {
        name, customFonts: { ...fonts, '--bg-image-opacity': String(bgImageOpacity) }, customFontWeight: null, customColors: colors, customBackgroundImage: backgroundImage, customSectionBackgrounds: sectionBackgrounds, customSections,
      });
      const newTheme = res.data;
      setCurrentId(newTheme._id);
      setCurrentTitle(newTheme.themeName);
      setHasForkedInSession(true);
      setForkOpen(false);
      setForkName('');
      lastSavedSnapshotRef.current = currentSnapshot;
      lastSavedFontsRef.current = { ...fonts, '--bg-image-opacity': String(bgImageOpacity) };
      lastSavedColorsRef.current = { ...colors };
      lastSavedMasterTitleRef.current = masterThemeTitle || currentTitle;
      lastSavedBgImageRef.current = backgroundImage;
      lastSavedBackgroundsRef.current = { ...sectionBackgrounds };
      lastSavedSectionsRef.current = { ...customSections };
      toast.success(`Saved & activated as "${newTheme.themeName}"`);
      onSaved?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create variant');
    } finally {
      setForking(false);
    }
  };

  // ── Activate: existing variant → save customisations + activate (single atomic call) ─────────
  const handleActivate = async () => {
    setForking(true);
    try {
      await axios.put(`${BASE_URL}/api/user-theme/${currentId}/activate`, {
        customFonts: { ...fonts, '--bg-image-opacity': String(bgImageOpacity) }, customFontWeight: null, customColors: colors, customBackgroundImage: backgroundImage, customSectionBackgrounds: sectionBackgrounds, customSections,
      });
      lastSavedSnapshotRef.current = currentSnapshot;
      lastSavedFontsRef.current = { ...fonts, '--bg-image-opacity': String(bgImageOpacity) };
      lastSavedColorsRef.current = { ...colors };
      lastSavedMasterTitleRef.current = masterThemeTitle || currentTitle;
      lastSavedBgImageRef.current = backgroundImage;
      lastSavedBackgroundsRef.current = { ...sectionBackgrounds };
      lastSavedSectionsRef.current = { ...customSections };
      setHasForkedInSession(true);
      toast.success(`"${currentTitle}" updated & activated!`);
      onSaved?.();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to activate');
    } finally {
      setForking(false);
    }
  };

  const handleBgOpacityChange = (sectionKey: string, value: number) => {
    const entry = sectionBackgrounds[sectionKey];
    if (!entry) return;
    const next = { ...sectionBackgrounds, [sectionKey]: { ...entry, opacity: value } };
    setSectionBackgrounds(next);
    sendPreview(fonts, colors, next);
  };

  // Reset clears the working form only — no DB write
  const handleReset = () => {
    setFonts({});
    setColors({});
    setCustomSections({});
    setSectionBackgrounds({});
    setAllBgOpacity(BG_DEFAULT_OPACITY);
    setForkOpen(false);
    setForkName('');
    setOpenSection('fonts');
    setOpenTypographyCard('heading');
    setOpenSizeSection(null);
    setOpenBgSection(null);
    // Don't clear lastSavedSnapshotRef — the reset state differs from saved,
    // so hasChanges will correctly be true and the button will offer Save.
    sendPreview({}, {}, {}, {});
  };

  // Reset one section's text sizes to theme default (100%) — form only, no DB write
  const handleResetSectionSizes = (sectionKey: string) => {
    if (!customSections[sectionKey]) return;
    const next = { ...customSections };
    delete next[sectionKey];
    setCustomSections(next);
    sendPreview(fonts, colors, sectionBackgrounds, next);
  };

  const anySizeSet = visibleSizeSections.some(s => SIZE_ROLES.some(r => readSizePct(customSections, s.key, r.key) != null));
  const anyBgSet = visibleBgSections.some(s => !!sectionBackgrounds[s.key]);
  const bgCount = visibleBgSections.filter(s => !!sectionBackgrounds[s.key]).length;

  if (!open) return null;

  return (
    <div className="fixed left-0 top-0 bottom-0 z-150 flex flex-col bg-card border-r border-border shadow-2xl overflow-hidden animate-in slide-in-from-left duration-200" style={{ width: '520px' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
        <div>
          <h2 className="text-sm font-semibold">Customise Theme</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{currentTitle}</p>
        </div>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

        {/* ── Typography panel ── */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
            onClick={() => setOpenSection(openSection === 'fonts' ? null : 'fonts')}
          >
            <span className="flex items-center gap-2">
              <Type className="w-4 h-4 opacity-60" /> Typography
            </span>
            {openSection === 'fonts' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {openSection === 'fonts' && (
            <div className="px-4 py-4 space-y-4">
              {fontsLoading && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Spinner size="xs" /> Loading 1,900+ Google Fonts…
                </p>
              )}
              {!fontsLoading && fontList.length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  Font picker unavailable — contact your admin to set up the Google Fonts API key.
                </p>
              )}

              {FONT_ROLES.map(role => {
                const isOpen = openTypographyCard === role.fontKey;
                return (
                  <div key={role.fontKey} className="border border-border rounded-xl overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
                      onClick={() => setOpenTypographyCard(isOpen ? null : role.fontKey)}
                    >
                      <span className="flex items-center gap-2">
                        <Type className="w-4 h-4 opacity-60" /> {role.label}
                      </span>
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    {isOpen && (
                      <div className="px-4 py-4">
                        <FontPickerCard
                          role={role}
                          currentFont={fonts[role.fontKey] || ''}
                          currentColors={colors}
                          currentWeight={fonts[role.weightKey] || ''}
                          fontList={fontList}
                          fontsLoading={fontsLoading}
                          themeDefaults={themeDefaults}
                          onFontChange={v => handleFontChange(role.fontKey, v)}
                          onColorChange={handleColorChange}
                          onClearColor={handleClearColor}
                          onWeightChange={handleWeightChange}
                          onGradientChange={role.isGradient ? (hex, startKey) => handleGradientChange(hex, startKey) : undefined}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Text sizes — collapsible per-section / per-role scale sliders ── */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="border border-border rounded-xl overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
                    onClick={() => setTextSizesOpen(v => !v)}
                  >
                    <span className="flex items-center gap-2">
                      <Type className="w-4 h-4 opacity-60" /> Text sizes
                      <span className={`text-[10px] font-mono font-normal ${anySizeSet ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                        {anySizeSet ? 'Custom' : 'Default'}
                      </span>
                    </span>
                    {textSizesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {textSizesOpen && (
                    <div className="px-3 py-3 border-t border-border/50">
                      <p className="text-[11px] text-muted-foreground mb-2">
                        Scale text per section and role. Anything left at 100% keeps the theme&apos;s design.
                      </p>

                      {/* Per-section cards — scrollable so the expansion stays contained */}
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {visibleSizeSections.map(section => {
                          const isOpen = openSizeSection === section.key;
                          const anySet = SIZE_ROLES.some(r => readSizePct(customSections, section.key, r.key) != null);
                          return (
                            <div key={section.key} className="border border-border rounded-xl overflow-hidden">
                              <div className="flex items-center bg-muted/40 hover:bg-muted/60 transition-colors">
                                <button
                                  type="button"
                                  className="flex-1 flex items-center justify-between px-3 py-2.5 text-xs font-medium min-w-0"
                                  onClick={() => setOpenSizeSection(isOpen ? null : section.key)}
                                >
                                  <span className="flex items-center gap-2">
                                    <Type className="w-3.5 h-3.5 opacity-60" /> {section.label}
                                  </span>
                                  <span className="flex items-center gap-2">
                                    <span className={`text-[10px] font-mono ${anySet ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                                      {anySet ? 'Custom' : 'Default'}
                                    </span>
                                    {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </span>
                                </button>
                                {anySet && (
                                  <button
                                    type="button"
                                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 mr-1 rounded"
                                    onClick={() => handleResetSectionSizes(section.key)}
                                    title={`Reset ${section.label} text sizes to 100%`}
                                  >
                                    <RotateCcw className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              {isOpen && (
                                <div className="px-3 py-3 space-y-3 border-t border-border/50">
                                  {SIZE_ROLES.filter(role => !section.hideRoles?.includes(role.key)).map(role => (
                                    <SizeSliderRow
                                      key={role.key}
                                      label={role.label}
                                      value={readSizePct(customSections, section.key, role.key)}
                                      onChange={pct => handleSizeChange(section.key, role.key, pct)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Buttons — one global control for every primary button */}
                        <div className="border border-border rounded-xl overflow-hidden">
                          <div className="flex items-center bg-muted/40 hover:bg-muted/60 transition-colors">
                            <button
                              type="button"
                              className="flex-1 flex items-center justify-between px-3 py-2.5 text-xs font-medium min-w-0"
                              onClick={() => setOpenSizeSection(openSizeSection === 'buttons' ? null : 'buttons')}
                            >
                              <span className="flex items-center gap-2">
                                <Type className="w-3.5 h-3.5 opacity-60" /> Buttons
                              </span>
                              <span className="flex items-center gap-2">
                                <span className={`text-[10px] font-mono ${buttonScalePct != null ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                                  {buttonScalePct != null ? 'Custom' : 'Default'}
                                </span>
                                {openSizeSection === 'buttons' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                              </span>
                            </button>
                            {buttonScalePct != null && (
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 mr-1 rounded"
                                onClick={() => handleButtonScaleChange(null)}
                                title="Reset button text size to 100%"
                              >
                                <RotateCcw className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          {openSizeSection === 'buttons' && (
                            <div className="px-3 py-3 space-y-3 border-t border-border/50">
                              <SizeSliderRow
                                label="Text size"
                                value={buttonScalePct}
                                onChange={handleButtonScaleChange}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Colours panel ── */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
            onClick={() => setOpenSection(openSection === 'colors' ? null : 'colors')}
          >
            <span className="flex items-center gap-2">
              <span className="flex gap-0.5">
                {['#e74c3c','#3498db','#2ecc71','#f39c12'].map(c => (
                  <span key={c} className="block w-2.5 h-4 rounded-sm" style={{ background: c }} />
                ))}
              </span>
              Visual Customisation
            </span>
            {openSection === 'colors' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {openSection === 'colors' && (
            <div className="px-4 py-4 space-y-5">
              {/* ── Accent group: non-gradient colours + primary button gradient ── */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Accent</p>
                <div className="space-y-2">
                  {COLOR_KEYS.filter(c => c.group === 'Accent').map(({ key, label }) => {
                      const hexColor = colors[key] ? hslToHex(colors[key]) : '';
                      const defaultHex = themeDefaults[key] ? hslToHex(themeDefaults[key]) : '';
                      const displayHex = hexColor || defaultHex;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <label className="flex-1 flex items-center gap-3 cursor-pointer group rounded-lg border border-border hover:border-primary/40 px-3 py-2.5 transition-colors bg-card">
                            <span
                              className="block w-8 h-8 rounded-md border-2 shrink-0 shadow-sm transition-all"
                              style={{
                                background: displayHex || '#888',
                                borderColor: hexColor ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium leading-tight">{label}</p>
                              <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">
                                {hexColor || (defaultHex ? `${defaultHex} (default)` : 'no default')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
                              <Pipette className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Pick</span>
                            </div>
                            <input
                              type="color"
                              className="sr-only"
                              value={displayHex || '#888888'}
                              onChange={e => handleColorChange(key, e.target.value)}
                            />
                          </label>
                          {hexColor && (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 rounded"
                              onClick={() => handleClearColor(key)}
                              title="Reset to theme default"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                  })}
                </div>
                {/* Primary Button Gradient — simplified/advanced picker */}
                <div className="mt-3">
                  <p className="text-[11px] font-medium text-muted-foreground mb-2">Primary button gradient</p>
                  <GradientColorPicker
                    gradientKeys={PRIMARY_BUTTON_GRADIENT}
                    currentColors={colors}
                    themeDefaults={themeDefaults}
                    onColorChange={handleColorChange}
                    onClearColor={handleClearColor}
                    onGradientChange={handleGradientChange}
                  />
                </div>
                <div className="mt-3">
                  <p className="text-[11px] font-medium text-muted-foreground mb-2">Accent highlight gradient</p>
                  <GradientColorPicker
                    gradientKeys={ACCENT_GRADIENT}
                    currentColors={colors}
                    themeDefaults={themeDefaults}
                    onColorChange={handleColorChange}
                    onClearColor={handleClearColor}
                    onGradientChange={handleGradientChange}
                  />
                </div>
              </div>

              {/* ── Remaining colour groups (Background, Text) ── */}
              {COLOR_GROUPS.filter(g => g !== 'Accent').map(group => (
                <div key={group}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{group}</p>
                  <div className="space-y-2">
                    {COLOR_KEYS.filter(c => c.group === group).map(({ key, label }) => {
                      const hexColor = colors[key] ? hslToHex(colors[key]) : '';
                      const defaultHex = themeDefaults[key] ? hslToHex(themeDefaults[key]) : '';
                      const displayHex = hexColor || defaultHex;
                      return (
                        <div key={key} className="flex items-center gap-2">
                          <label className="flex-1 flex items-center gap-3 cursor-pointer group rounded-lg border border-border hover:border-primary/40 px-3 py-2.5 transition-colors bg-card">
                            <span
                              className="block w-8 h-8 rounded-md border-2 shrink-0 shadow-sm transition-all"
                              style={{
                                background: displayHex || '#888',
                                borderColor: hexColor ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium leading-tight">{label}</p>
                              <p className="text-[10px] text-muted-foreground font-mono truncate mt-0.5">
                                {hexColor || (defaultHex ? `${defaultHex} (default)` : 'no default')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground transition-colors shrink-0">
                              <Pipette className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Pick</span>
                            </div>
                            <input
                              type="color"
                              className="sr-only"
                              value={displayHex || '#888888'}
                              onChange={e => handleColorChange(key, e.target.value)}
                            />
                          </label>
                          {hexColor && (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 rounded"
                              onClick={() => handleClearColor(key)}
                              title="Reset to theme default"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

            </div>
          )}
        </div>

        {/* ── Backgrounds panel — per-section background images ── */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
            onClick={() => setOpenSection(openSection === 'backgrounds' ? null : 'backgrounds')}
          >
            <span className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 opacity-60" /> Section backgrounds
              <span className={`text-[10px] font-mono font-normal ${anyBgSet ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                {anyBgSet ? 'Custom' : 'Default'}
              </span>
            </span>
            {openSection === 'backgrounds' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {openSection === 'backgrounds' && (
            <div className="px-3 py-3 border-t border-border/50 space-y-3">
              <p className="text-[11px] text-muted-foreground">
                Give any section its own background image. Sections without one keep the theme&apos;s colours.
              </p>

              {/* Real-time upload progress (0–100) */}
              {bgUploadStatus && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-medium text-foreground">{bgUploadStatus.label}</p>
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {bgUploadStatus.progress != null ? `${Math.min(100, Math.max(0, bgUploadStatus.progress))}%` : '…'}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-border overflow-hidden">
                    {bgUploadStatus.progress != null ? (
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-200"
                        style={{ width: `${Math.min(100, Math.max(0, bgUploadStatus.progress))}%` }}
                      />
                    ) : (
                      <div className="h-full w-full rounded-full bg-primary animate-pulse" />
                    )}
                  </div>
                </div>
              )}

              {/* Shared crop step — replaces the list while a crop is in progress */}
              {bgUploadStep === 'cropping' && bgSelectedFile ? (
                <div className="space-y-2">
                  <Card className="flex flex-col items-center">
                    <div className="w-full border-b p-3">
                      <p className="text-sm font-medium text-foreground text-center">
                        Adjust crop area — Drag to move, edges to resize
                      </p>
                    </div>
                    <div className="w-full flex justify-center py-3">
                      <Cropper
                        file={bgSelectedFile}
                        aspect={16 / 9}
                        maxImageSize={5 * 1024 * 1024}
                        keepSelection
                        onCrop={handleBgCropComplete}
                        onCropRef={bgCropperRef}
                      />
                    </div>
                  </Card>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={async () => {
                        // Show the spinner BEFORE the blocking canvas work.
                        // applyCrop() calls canvas.toDataURL() synchronously,
                        // which freezes the main thread for large images.
                        // Without this yield, the spinner never paints until
                        // after the heavy work is already done.
                        setBgUploading(true);
                        await new Promise(r => setTimeout(r, 50));
                        try {
                          await bgCropperRef.current?.applyCrop();
                        } catch {
                          setBgUploading(false);
                          toast.error('Failed to process image');
                        }
                      }}
                      disabled={bgUploading}
                      size="sm"
                    >
                      {bgUploading ? (
                        <>
                          <Spinner size="xs" className="mr-1.5" />
                          {bgUploadStatus?.progress != null ? `Uploading… ${bgUploadStatus.progress}%` : (bgUploadStatus?.label || 'Uploading…')}
                        </>
                      ) : (
                        'Upload'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetBgUpload}
                      disabled={bgUploading}
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Apply to all — bulk-set the same background on every section */}
                  <div className="border border-border rounded-xl overflow-hidden">
                    <div className="flex items-center bg-muted/40 hover:bg-muted/60 transition-colors">
                      <button
                        type="button"
                        className="flex-1 flex items-center justify-between px-3 py-2.5 text-xs font-medium min-w-0"
                        onClick={() => setOpenBgSection(openBgSection === BG_ALL_SECTIONS ? null : BG_ALL_SECTIONS)}
                      >
                        <span className="flex items-center gap-2">
                          <ImageIcon className="w-3.5 h-3.5 opacity-60" /> Apply to all
                        </span>
                        <span className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono ${anyBgSet ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                            {anyBgSet ? `${bgCount} set` : 'Default'}
                          </span>
                          {openBgSection === BG_ALL_SECTIONS ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </span>
                      </button>
                      {anyBgSet && (
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 mr-1 rounded"
                          onClick={handleClearAllBackgrounds}
                          title="Clear all section backgrounds"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {openBgSection === BG_ALL_SECTIONS && (
                      <div className="px-3 py-3 space-y-3 border-t border-border/50">
                        <p className="text-[11px] text-muted-foreground">
                          Upload once to use the same background on every section below. Individual sections can still be changed or removed.
                        </p>
                        <Card
                          className="group flex w-full flex-col items-center justify-center gap-3 py-6 border-dashed text-xs cursor-pointer hover:bg-muted/50 transition-colors"
                          onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('bg-muted/50');
                          }}
                          onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                            e.currentTarget.classList.remove('bg-muted/50');
                          }}
                          onDrop={(e) => handleBgDrop(e, BG_ALL_SECTIONS)}
                          onClick={() => openBgFileUpload(BG_ALL_SECTIONS)}
                        >
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-4" />
                            <div>
                              Drop image here or{' '}
                              <span className="text-primary underline underline-offset-2">browse</span> to upload
                            </div>
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            JPG, PNG, WebP (max 5 MB) · 16:9 recommended
                          </span>
                        </Card>
                        {anyBgSet && (
                          <div>
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-[11px] font-medium text-muted-foreground">Opacity — all sections</p>
                              <span className="text-[11px] font-mono text-muted-foreground">{allBgOpacity}%</span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={allBgOpacity}
                              onChange={e => handleAllOpacityChange(Number(e.target.value))}
                              className="w-full h-1.5 rounded-full appearance-none bg-border cursor-pointer accent-primary"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {visibleBgSections.map(section => {
                    const entry = sectionBackgrounds[section.key];
                    const isOpen = openBgSection === section.key;
                    return (
                      <div key={section.key} className="border border-border rounded-xl overflow-hidden">
                        <div className="flex items-center bg-muted/40 hover:bg-muted/60 transition-colors">
                          <button
                            type="button"
                            className="flex-1 flex items-center justify-between px-3 py-2.5 text-xs font-medium min-w-0"
                            onClick={() => setOpenBgSection(isOpen ? null : section.key)}
                          >
                            <span className="flex items-center gap-2">
                              <ImageIcon className="w-3.5 h-3.5 opacity-60" /> {section.label}
                            </span>
                            <span className="flex items-center gap-2">
                              {entry && (
                                entry.video ? (
                                  <video src={entry.video} muted playsInline className="w-10 h-6 rounded object-cover border border-border" />
                                ) : (
                                  <img src={entry.image} alt="" className="w-10 h-6 rounded object-cover border border-border" />
                                )
                              )}
                              <span className={`text-[10px] font-mono ${entry ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                                {entry ? 'Custom' : 'Default'}
                              </span>
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </span>
                          </button>
                          {entry && (
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1.5 mr-1 rounded"
                              onClick={() => handleRemoveBg(section.key)}
                              title={`Remove ${section.label} background`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        {isOpen && (
                          <div className="px-3 py-3 space-y-3 border-t border-border/50">
                            {!entry ? (
                              <Card
                                className="group flex w-full flex-col items-center justify-center gap-3 py-6 border-dashed text-xs cursor-pointer hover:bg-muted/50 transition-colors"
                                onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                                  e.preventDefault();
                                  e.currentTarget.classList.add('bg-muted/50');
                                }}
                                onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                                  e.currentTarget.classList.remove('bg-muted/50');
                                }}
                                onDrop={(e) => handleBgDrop(e, section.key)}
                                onClick={() => openBgFileUpload(section.key)}
                              >
                                <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                                  <Upload className="size-4" />
                                  <div>
                                    {section.key === 'hero' ? 'Drop image or video here' : 'Drop image here'}{' '}
                                    <span className="text-primary underline underline-offset-2">browse</span> to upload
                                  </div>
                                </div>
                                <span className="text-[10px] text-muted-foreground">
                                  {section.key === 'hero'
                                    ? 'JPG, PNG, WebP (max 5 MB) or MP4 / WebM / MOV video (max 30 MB) · 16:9 recommended'
                                    : 'JPG, PNG, WebP (max 5 MB) · 16:9 recommended'}
                                </span>
                              </Card>
                            ) : (
                              <>
                                <div
                                  className="relative w-full rounded-xl overflow-hidden cursor-pointer"
                                  style={{
                                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                                    backgroundSize: '16px 16px',
                                    backgroundPosition: '0 0, 8px 8px',
                                    backgroundRepeat: 'repeat',
                                  }}
                                  onClick={() => openBgFileUpload(section.key)}
                                  title="Click to replace"
                                >
                                  {entry.video ? (
                                    <video
                                      src={entry.video}
                                      autoPlay
                                      loop
                                      muted
                                      playsInline
                                      className="w-full h-24 object-cover hover:opacity-80 transition-opacity"
                                    />
                                  ) : (
                                    <img
                                      src={entry.image}
                                      alt={`${section.label} background`}
                                      className="w-full h-24 object-cover hover:opacity-80 transition-opacity"
                                    />
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-foreground"
                                    onClick={() => openBgFileUpload(section.key)}
                                  >
                                    <RefreshCw className="size-3.5" />
                                    Change
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-destructive"
                                    onClick={() => handleRemoveBg(section.key)}
                                  >
                                    <Trash2 className="size-3.5" />
                                    Remove
                                  </Button>
                                </div>
                                <div>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-[11px] font-medium text-muted-foreground">Opacity</p>
                                    <span className="text-[11px] font-mono text-muted-foreground">{entry.opacity}%</span>
                                  </div>
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={entry.opacity}
                                    onChange={e => handleBgOpacityChange(section.key, Number(e.target.value))}
                                    className="w-full h-1.5 rounded-full appearance-none bg-border cursor-pointer accent-primary"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
              <input
                ref={bgFileInputRef}
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.webp,.mp4,.webm,.mov"
                onChange={handleBgFileChange}
              />
            </div>
          )}
        </div>

        {/* ── Frames & Buttons panel ── */}
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium bg-muted/40 hover:bg-muted/60 transition-colors"
            onClick={() => setOpenSection(openSection === 'buttons' ? null : 'buttons')}
          >
            <span className="flex items-center gap-2">
              <span className="flex h-5 w-8 items-center justify-center rounded-md border border-current text-[9px] opacity-60">Aa</span>
              Frames &amp; Buttons
            </span>
            {openSection === 'buttons' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {openSection === 'buttons' && (
            <div className="px-4 py-4 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">All shapes</p>
                  {getMasterRadiusMode() === 'mixed' && <span className="text-[10px] font-semibold text-primary">Mixed</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mb-3">Set every structural shape at once, then override individual groups below.</p>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    ['default', 'Theme default', '6px'],
                    ['rounded', 'Rounded', '9999px'],
                    ['square', 'Sharp', '0px'],
                    ['custom', 'Custom', '12px'],
                  ] as const).map(([mode, label, previewRadius]) => {
                    const selected = getMasterRadiusMode() === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => handleMasterRadiusMode(mode)}
                        className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                          selected ? 'border-primary bg-primary/10 text-foreground' : 'border-border hover:border-primary/40'
                        }`}
                      >
                        <span
                          className="flex h-7 w-14 shrink-0 items-center justify-center bg-primary text-[8px] font-semibold text-primary-foreground transition-[border-radius]"
                          style={{ borderRadius: previewRadius }}
                        >
                          Button
                        </span>
                        <span className="text-xs font-medium">
                          {label}
                          {mode === 'custom' && <span className="block text-[9px] font-normal text-muted-foreground">Adjust below</span>}
                        </span>
                        {selected && <Check className="ml-auto size-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                {RADIUS_GROUPS.map(group => {
                  const mode = getRadiusMode(fonts[group.key], group.roundedValue);
                  return (
                    <div key={group.key} className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
                      <div>
                        <p className="text-xs font-semibold">{group.label}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{group.description}</p>
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        {(['default', 'rounded', 'square', 'custom'] as const).map(option => {
                          const previewRadius = option === 'default'
                            ? '6px'
                            : option === 'rounded'
                              ? group.roundedValue
                              : option === 'square'
                                ? '0px'
                                : '12px';
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => handleRadiusMode(group, option)}
                              className={`min-w-0 border px-1.5 py-2 text-[10px] font-medium capitalize transition-all ${
                                mode === option ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:border-primary/40'
                              }`}
                              style={{ borderRadius: previewRadius }}
                            >
                              {option === 'square' ? 'Sharp' : option}
                            </button>
                          );
                        })}
                      </div>
                      {mode === 'custom' && (
                        <div>
                          <div className="mb-2 flex items-center justify-between">
                            <label htmlFor={`radius-${group.key}`} className="text-[11px] font-medium">Custom radius</label>
                            <span className="text-[11px] font-mono text-muted-foreground">{getCustomRadius(fonts[group.key], group.defaultCustom)}px</span>
                          </div>
                          <input
                            id={`radius-${group.key}`}
                            type="range"
                            min={0}
                            max={48}
                            step={1}
                            value={getCustomRadius(fonts[group.key], group.defaultCustom)}
                            onChange={e => handleCustomRadius(group.key, Number(e.target.value))}
                            className="w-full h-1.5 rounded-full appearance-none bg-border cursor-pointer accent-primary"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground">
                  Circular social, play, arrow and toggle controls always remain circular.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border shrink-0">

        {/* Fork name input — only shown for base themes before first save */}
        {forkOpen && !isCurrentVariant && (
          <div className="px-5 pt-4 pb-3 bg-muted/30 border-b border-border space-y-3">
            <div>
              <p className="text-xs font-semibold mb-0.5">Name your custom theme</p>
              <p className="text-[11px] text-muted-foreground">
                Any existing custom theme will be <span className="font-medium">replaced</span>. Base themes stay untouched.
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                value={forkName}
                onChange={e => setForkName(e.target.value)}
                placeholder={`e.g. ${themeTitle} by ${siteName || 'me'}`}
                className="h-8 text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleFork()}
                autoFocus
              />
              <Button size="sm" className="gap-1.5 shrink-0" onClick={handleFork} disabled={forking || !forkName.trim()}>
                {forking ? <Spinner size="xs" className="text-inherit" /> : <Check className="w-3.5 h-3.5" />}
                Activate
              </Button>
              <Button size="sm" variant="ghost" className="px-2" onClick={() => setForkOpen(false)} disabled={forking}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 px-5 py-3">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleReset}
            disabled={forking}
            title="Clear all customizations (form only — nothing is saved)"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>

          {hasChanges ? (
            // Unsaved changes — always offer save
            canSave ? (
              // Already a variant (or forked this session) — save in place
              <Button size="sm" className="flex-1 gap-1.5" onClick={handleActivate} disabled={forking}>
                {forking ? <Spinner size="xs" className="text-inherit" /> : <Check className="w-3.5 h-3.5" />}
                Save & Activate
              </Button>
            ) : (
              // Base theme — need to fork first
              <Button
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => setForkOpen(v => !v)}
                disabled={forking}
              >
                <BookmarkPlus className="w-3.5 h-3.5" />
                {forkOpen ? 'Cancel' : 'Save as & Activate'}
              </Button>
            )
          ) : (
            // No changes — offer Close
            <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={onClose}>
              <Check className="w-3.5 h-3.5" /> Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizerDrawer;
